// landing/sections/CouncilSection.tsx — the mechanism, told plainly.
//
// This is the platform's actual differentiator (GROWTH-PLAN §3.1): a jury of
// real members deciding moderation cases in public. Everything claimed here
// must stay true of the product — the section links to /council and /policies
// so a sceptical reader can check the receipts rather than take our word.

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ConsolePanel } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Contrast = styled.p`
  margin: 0;
  max-width: 62ch;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Cards = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

const Card = styled(ConsolePanel).attrs({ as: 'li' })`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};

  svg {
    color: ${({ theme }) => theme.color.accentCyan};
    flex-shrink: 0;
  }
`;

const CardBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Receipts = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};

  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: none;
    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
      outline-offset: 2px;
    }
  }
`;

const POINTS: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'users-round',
    title: 'A jury of members, not an algorithm',
    body:
      'Cases are decided by a randomly assembled team of real members. Teams rotate; nobody moderates from a permanent seat, and nobody judges a case they are part of.',
  },
  {
    icon: 'file-text',
    title: 'Decisions in public, with written reasons',
    body:
      'Every decision comes with a written explanation you can read. If you disagree with one, you can appeal it, and the appeal is decided the same way.',
  },
  {
    icon: 'eye',
    title: 'Nothing hidden while they decide',
    body:
      'A reported post stays fully visible with an "Under Review" label until a decision is made. A report is an accusation, not a verdict.',
  },
  {
    icon: 'shield',
    title: 'Five rules. The list does not grow quietly.',
    body:
      'Only five things are ever actionable, and they are written down. Being wrong, rude, or unpopular is not one of them.',
  },
];

export function CouncilSection() {
  return (
    <Wrap aria-labelledby="landing-council-heading">
      <SectionHeading id="landing-council-heading">How moderation works here</SectionHeading>
      <Contrast>
        Most platforms make you choose: a free-for-all that turns toxic, or safety
        enforced by heavy-handed, invisible rules. TruXSocial refuses both. Respect for
        everyone, including the moderators, is a design principle here, not a slogan.
      </Contrast>
      <Cards>
        {POINTS.map((p) => (
          <Card key={p.title}>
            <CardTitle>
              <Icon name={p.icon} size={16} />
              {p.title}
            </CardTitle>
            <CardBody>{p.body}</CardBody>
          </Card>
        ))}
      </Cards>
      <Receipts>
        Don&apos;t take our word for it: <Link to="/council">see the Council</Link> or{' '}
        <Link to="/policies">read the rules</Link>, no account needed for either.
      </Receipts>
    </Wrap>
  );
}
